// src/pages/Dashboard.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Row,
  Col,
  Card,
  Statistic,
  Tag,
  Typography,
  Space,
  Avatar,
  message,
  Tooltip,
  Button,
  Table,
  Input,
  Select,
  DatePicker,
  Segmented,
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  RiseOutlined,
  PlusOutlined,
  CalendarOutlined,
  ReloadOutlined,
  SearchOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import StandardPage from '../components/Layout/StandardPage';
import CreateActivityDrawer from '../components/activities/CreateActivityDrawer';
import ActivityDetailsDrawer from '../components/activities/ActivityDetailsDrawer';

import { listCustomers } from '../services/customers';
import { getTeam } from '../services/users';
import { getActivitiesCount, listActivities, listAllActivities, type ActivityDTO } from '../services/activities';
import { useAuth } from '../context/AuthContext';
import { renderAntdIcon } from '../utils/antdIcon';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';


const { Text } = Typography;
const { RangePicker } = DatePicker;

type RangeValue = [Dayjs | null, Dayjs | null] | null;

interface DashboardStats {
  totalCustomers: number;
  activeCustomers: number;
  totalActivities: number;
  thisWeekActivities: number;
  lastWeekActivities: number;
  thisMonthActivities: number;
  teamMembers: number;
}

type CustomerActivityRow = {
  key: number;
  customerId: number;
  customerName: string;
  totalActivities: number;
  lastActivityAt?: string;
  lastActivityName?: string;
  typeBreakdown: Array<{ code: string; label: string; count: number }>;
  recentActivities: ActivityDTO[];
};

const getCurrentMonthRange = (): [Dayjs, Dayjs] => [
  dayjs().startOf('month'),
  dayjs().endOf('month'),
];

const Dashboard: React.FC = () => {
  const [activityOpen, setActivityOpen] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [myLoading, setMyLoading] = useState(false);
  const [stats, setStats] = useState<DashboardStats>({
    totalCustomers: 0,
    activeCustomers: 0,
    totalActivities: 0,
    thisWeekActivities: 0,
    lastWeekActivities: 0,
    thisMonthActivities: 0,
    teamMembers: 0,
  });
  const [activities, setActivities] = useState<ActivityDTO[]>([]);
  const [myActivitiesPage, setMyActivitiesPage] = useState<ActivityDTO[]>([]);
  const [myTotal, setMyTotal] = useState(0);
  const [myPage, setMyPage] = useState(1);
  const [myPageSize, setMyPageSize] = useState(10);
  const [dateRange, setDateRange] = useState<RangeValue>(() => getCurrentMonthRange());
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [myDateRange, setMyDateRange] = useState<RangeValue>(() => getCurrentMonthRange());
  const [myTypeFilter, setMyTypeFilter] = useState<string[]>([]);
  const [mySearchTerm, setMySearchTerm] = useState('');
  const [activityView, setActivityView] = useState<'customer' | 'mine'>('customer');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState<number | null>(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const loading = statsLoading || customerLoading || myLoading;
  const statsInFlightRef = useRef(false);
  const customerFetchKeyRef = useRef<string>('');
  const customerInFlightRef = useRef(false);

  const fetchStats = useCallback(async () => {
    if (statsInFlightRef.current) return;
    statsInFlightRef.current = true;
    try {
      setStatsLoading(true);
      const now = dayjs();
      const thisWeekStart = now.startOf('week');

      const [
        customerList,
        team,
        thisWeekActivities,
      ] = await Promise.all([
        listCustomers(),
        getTeam(),
        getActivitiesCount(
          { from: thisWeekStart.toISOString(), to: now.toISOString() },
          { useCountEndpoint: true }
        ),
      ]);

      setStats(prev => ({
        ...prev,
        totalCustomers: customerList.length,
        thisWeekActivities,
        lastWeekActivities: 0,
        teamMembers: team.filter(t => t.isActive && t.customerId === null).length,
      }));
    } catch (error: any) {
      message.error(error?.message || 'Errore caricamento dashboard');
    } finally {
      setStatsLoading(false);
      statsInFlightRef.current = false;
    }
  }, []);

  const fetchCustomerActivities = useCallback(async () => {
    if (!dateRange?.[0] || !dateRange?.[1]) return;
    const fromKey = dateRange[0].startOf('day').toISOString();
    const toKey = dateRange[1].endOf('day').toISOString();
    const fetchKey = `${fromKey}|${toKey}`;
    if (customerInFlightRef.current && customerFetchKeyRef.current === fetchKey) return;
    customerInFlightRef.current = true;
    customerFetchKeyRef.current = fetchKey;
    try {
      setCustomerLoading(true);
      const from = fromKey;
      const to = toKey;
      const allActivities = await listAllActivities({
        from,
        to,
        sort: 'occurredAt,desc',
        size: 500,
        maxPages: 50,
      });
      allActivities.sort((a, b) => dayjs(b.occurredAt).valueOf() - dayjs(a.occurredAt).valueOf());
      setActivities(allActivities);

      const customersWithActivities = new Set<number>();
      allActivities.forEach((activity) => {
        if (activity.customerId == null) return;
        customersWithActivities.add(activity.customerId);
      });

      setStats(prev => ({
        ...prev,
        activeCustomers: customersWithActivities.size,
        thisMonthActivities: allActivities.length,
      }));
    } catch (error: any) {
      message.error(error?.message || 'Errore caricamento attivita');
    } finally {
      setCustomerLoading(false);
      if (customerFetchKeyRef.current === fetchKey) {
        customerInFlightRef.current = false;
      }
    }
  }, [dateRange]);

  const fetchMyActivitiesPage = useCallback(async () => {
    if (!user?.id) {
      setMyActivitiesPage([]);
      setMyTotal(0);
      return;
    }
    try {
      setMyLoading(true);
      const [start, end] = myDateRange ?? [];
      const from = start ? start.startOf('day').toISOString() : undefined;
      const to = end ? end.endOf('day').toISOString() : undefined;
      const page = await listActivities({
        page: myPage - 1,
        size: myPageSize,
        sort: 'occurredAt,desc',
        from,
        to,
        userId: user.id,
      });
      setMyActivitiesPage(page.content ?? []);
      setMyTotal(page.totalElements ?? page.content?.length ?? 0);
    } catch (error: any) {
      message.error(error?.message || 'Errore caricamento attivita');
    } finally {
      setMyLoading(false);
    }
  }, [user?.id, myDateRange, myPage, myPageSize]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchCustomerActivities();
  }, [fetchCustomerActivities]);

  useEffect(() => {
    if (activityView === 'mine') {
      fetchMyActivitiesPage();
    }
  }, [fetchMyActivitiesPage, activityView]);

  useEffect(() => {
    setMyPage(1);
  }, [myDateRange]);

  useEffect(() => {
    setMyPage(1);
  }, [myTypeFilter, mySearchTerm]);

  const refreshAll = useCallback(() => {
    fetchStats();
    fetchCustomerActivities();
    if (activityView === 'mine') {
      fetchMyActivitiesPage();
    }
  }, [fetchStats, fetchCustomerActivities, fetchMyActivitiesPage, activityView]);

  const handleRowClick = useCallback(
    (record: CustomerActivityRow) => {
      navigate(`/customers/${record.customerId}`);
    },
    [navigate],
  );

  const openActivityDetails = useCallback((activityId?: number | null) => {
    if (!activityId) return;
    setSelectedActivityId(activityId);
    setDetailsOpen(true);
  }, []);

  const weeklyTrend = stats.lastWeekActivities > 0
    ? ((stats.thisWeekActivities - stats.lastWeekActivities) / stats.lastWeekActivities * 100)
    : 0;

  const typeOptions = useMemo(() => {
    const map = new Map<string, string>();
    activities.forEach((activity) => {
      const code = activity.activityType?.code || activity.type;
      if (!code) return;
      if (!map.has(code)) {
        map.set(code, activity.activityType?.label || code);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));
  }, [activities]);

  const myTypeOptions = useMemo(() => {
    const map = new Map<string, string>();
    myActivitiesPage.forEach((activity) => {
      const code = activity.activityType?.code || activity.type;
      if (!code) return;
      if (!map.has(code)) {
        map.set(code, activity.activityType?.label || code);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'it', { sensitivity: 'base' }));
  }, [myActivitiesPage]);

  const tableData = useMemo<CustomerActivityRow[]>(() => {
    if (!activities.length) return [];

    const typeSet = new Set(typeFilter);

    const grouped = new Map<number, { customerName: string; activities: ActivityDTO[] }>();

    activities.forEach((activity) => {
      if (!activity.customerId) return;

      const code = activity.activityType?.code || activity.type || 'EXTRA';
      if (typeSet.size && !typeSet.has(code)) return;

      const existing = grouped.get(activity.customerId);
      if (existing) {
        existing.activities.push(activity);
      } else {
        grouped.set(activity.customerId, {
          customerName: activity.customerName || 'Senza cliente',
          activities: [activity],
        });
      }
    });

    let rows = Array.from(grouped.entries()).map(([customerId, info]) => {
      const sorted = info.activities
        .slice()
        .sort((a, b) => dayjs(b.occurredAt).valueOf() - dayjs(a.occurredAt).valueOf());

      const breakdownMap = new Map<string, { label: string; count: number }>();
      sorted.forEach((act) => {
        const code = act.activityType?.code || act.type || 'EXTRA';
        const label = act.activityType?.label || code;
        const current = breakdownMap.get(code);
        if (current) {
          current.count += 1;
        } else {
          breakdownMap.set(code, { label, count: 1 });
        }
      });

      const typeBreakdown = Array.from(breakdownMap.entries())
        .map(([code, value]) => ({ code, label: value.label, count: value.count }))
        .sort((a, b) => b.count - a.count);

      const lastActivity = sorted[0];

      return {
        key: customerId,
        customerId,
        customerName: info.customerName,
        totalActivities: sorted.length,
        lastActivityAt: lastActivity?.occurredAt,
        lastActivityName:
          lastActivity?.name ||
          lastActivity?.activityType?.label ||
          lastActivity?.activityType?.code,
        typeBreakdown,
        recentActivities: sorted.slice(0, 3),
      };
    });

    if (searchTerm.trim()) {
      const query = searchTerm.trim().toLowerCase();
      rows = rows.filter((row) => row.customerName?.toLowerCase().includes(query));
    }

    rows.sort((a, b) => {
      if (!a.lastActivityAt && !b.lastActivityAt) return 0;
      if (!a.lastActivityAt) return 1;
      if (!b.lastActivityAt) return -1;
      return dayjs(b.lastActivityAt).valueOf() - dayjs(a.lastActivityAt).valueOf();
    });

    return rows;
  }, [activities, typeFilter, searchTerm]);

  const myTableData = useMemo<ActivityDTO[]>(() => {
    if (!myActivitiesPage.length) return [];

    const typeSet = new Set(myTypeFilter);

    let rows = myActivitiesPage.filter((activity) => {
      const code = activity.activityType?.code || activity.type || 'EXTRA';
      if (typeSet.size && !typeSet.has(code)) return false;

      return true;
    });

    if (mySearchTerm.trim()) {
      const query = mySearchTerm.trim().toLowerCase();
      rows = rows.filter((activity) => {
        const label =
          activity.name || activity.activityType?.label || activity.activityType?.code || '';
        const customer = activity.customerName || '';
        return (
          label.toLowerCase().includes(query) ||
          customer.toLowerCase().includes(query)
        );
      });
    }

    rows.sort(
      (a, b) => dayjs(b.occurredAt).valueOf() - dayjs(a.occurredAt).valueOf(),
    );

    return rows;
  }, [myActivitiesPage, myTypeFilter, mySearchTerm]);

  const columns: ColumnsType<CustomerActivityRow> = useMemo(
    () => [
      {
        title: 'Cliente',
        dataIndex: 'customerName',
        key: 'customer',
        sorter: (a, b) =>
          (a.customerName || '').localeCompare(b.customerName || '', 'it', {
            sensitivity: 'base',
          }),
        sortDirections: ['ascend', 'descend'],
        render: (_, row) => (
          <Space size={12}>
            <Avatar icon={<UserOutlined />} />
            <Space direction="vertical" size={0}>
              <Text strong>{row.customerName}</Text>
              <Text type="secondary">{row.totalActivities} Attività registrate</Text>
            </Space>
          </Space>
        ),
      },
      {
        title: 'Ultima attività',
        dataIndex: 'lastActivityAt',
        key: 'lastActivityAt',
        render: (_, row) =>
          row.lastActivityAt ? (
            <Space direction="vertical" size={0}>
              <Text strong>{row.lastActivityName || 'Attività'}</Text>
              <Text type="secondary">{dayjs(row.lastActivityAt).format('DD/MM/YYYY HH:mm')}</Text>
            </Space>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: 'Tipologie principali',
        dataIndex: 'typeBreakdown',
        key: 'typeBreakdown',
        render: (_, row) =>
          row.typeBreakdown.length ? (
            <Space wrap size={6}>
              {row.typeBreakdown.slice(0, 3).map((type) => (
                <Tag key={type.code} color="blue">
                  {type.label}
                  <span style={{ marginLeft: 4 }}>{type.count}</span>
                </Tag>
              ))}
              {row.typeBreakdown.length > 3 && (
                <Tooltip
                  title={row.typeBreakdown
                    .slice(3)
                    .map((type) => `${type.label}: ${type.count}`)
                    .join(', ')}
                >
                  <Tag>+{row.typeBreakdown.length - 3}</Tag>
                </Tooltip>
              )}
            </Space>
          ) : (
            <Text type="secondary">-</Text>
          ),
      },
      {
        title: 'Ultime attività registrate',
        dataIndex: 'recentActivities',
        key: 'recentActivities',
        render: (_, row) =>
          row.recentActivities.length ? (
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              {row.recentActivities.map((act) => (
                <div
                  key={act.id}
                  style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}
                >
                  <span>{act.name || act.activityType?.label || act.activityType?.code}</span>
                  <Text type="secondary">{dayjs(act.occurredAt).format('DD/MM')}</Text>
                </div>
              ))}
            </Space>
          ) : (
            <Text type="secondary">-</Text>
          ),},
    ],
    []
  );

  const myColumns: ColumnsType<ActivityDTO> = useMemo(
    () => [
      {
        title: 'Attività',
        dataIndex: 'name',
        key: 'name',
        sorter: (a, b) => {
          const labelA = a.name || a.activityType?.label || a.activityType?.code || '';
          const labelB = b.name || b.activityType?.label || b.activityType?.code || '';
          return labelA.localeCompare(labelB, 'it', { sensitivity: 'base' });
        },
        sortDirections: ['ascend', 'descend'],
        render: (_, row) => {
          const label = row.name || row.activityType?.label || row.activityType?.code || 'Attività';
          return (
            <Space size={12}>
              <span
                style={{
                  fontSize: 18,
                  color: '#1677ff',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                {renderAntdIcon(row.activityType?.icon)}
              </span>
              <Space direction="vertical" size={0}>
                <Text strong>{label}</Text>
               
              </Space>
            </Space>
          );
        },
      },
      {
        title: 'Cliente',
        dataIndex: 'customerName',
        key: 'customerName',
        sorter: (a, b) =>
          (a.customerName || '').localeCompare(b.customerName || '', 'it', {
            sensitivity: 'base',
          }),
        render: (_, row) => (
          <Text>{row.customerName || 'Senza cliente'}</Text>
        ),
      },
      {
        title: 'Quando',
        dataIndex: 'occurredAt',
        key: 'occurredAt',
        sorter: (a, b) => dayjs(a.occurredAt).valueOf() - dayjs(b.occurredAt).valueOf(),
        sortDirections: ['descend', 'ascend'],
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Text strong>{dayjs(row.occurredAt).format('DD/MM/YYYY')}</Text>
            <Text type="secondary">{dayjs(row.occurredAt).format('HH:mm')}</Text>
          </Space>
        ),
      },
      {
        title: 'Tipologia',
        dataIndex: 'activityType',
        key: 'activityType',
        render: (_, row) => {
          const label = row.activityType?.label || row.activityType?.code || row.type || 'EXTRA';
          return <Tag color="blue">{label}</Tag>;
        },
      },
    ],
    [],
  );

  const filtersApplied = useMemo(() => {
    const [start, end] = dateRange ?? [];
    const hasSearch = Boolean(searchTerm.trim());
    const hasTypes = typeFilter.length > 0;

    if (start && end) {
      const [defaultStart, defaultEnd] = getCurrentMonthRange();
      const isDefaultRange =
        start.isSame(defaultStart, 'day') && end.isSame(defaultEnd, 'day');
      return hasSearch || hasTypes || !isDefaultRange;
    }

    return hasSearch || hasTypes || Boolean(start || end);
  }, [dateRange, searchTerm, typeFilter]);

  const myFiltersApplied = useMemo(() => {
    const [start, end] = myDateRange ?? [];
    const hasSearch = Boolean(mySearchTerm.trim());
    const hasTypes = myTypeFilter.length > 0;

    if (start && end) {
      const [defaultStart, defaultEnd] = getCurrentMonthRange();
      const isDefaultRange =
        start.isSame(defaultStart, 'day') && end.isSame(defaultEnd, 'day');
      return hasSearch || hasTypes || !isDefaultRange;
    }

    return hasSearch || hasTypes || Boolean(start || end);
  }, [myDateRange, mySearchTerm, myTypeFilter]);

  const actions = [
    {
      label: 'Nuova Attività',
      type: 'primary' as const,
      icon: <PlusOutlined />,
      onClick: () => setActivityOpen(true),
    },
    {
      label: 'Ricarica',
      type: 'default' as const,
      icon: <ReloadOutlined />,
      onClick: () => refreshAll(),
    }
  ];

  const breadcrumbs = [{ title: 'Home' }, { title: 'Dashboard' }];
  return (
    <>
      <StandardPage
        title="Dashboard"
        subtitle="Panoramica delle attività e performance"
        breadcrumbs={breadcrumbs}
        actions={actions}
      >
        {/* KPI Cards */}
        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{
                borderRadius: 16,
                
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Statistic style={{minHeight:100}}
                title={<span style={{ color: 'rgba(255,255,255,0.8)' }}>Clienti Totali</span>}
                value={stats.totalCustomers}
                loading={loading}
                prefix={<UserOutlined style={{ color: 'white' }} />}
                valueStyle={{ color: 'white', fontSize: '28px', fontWeight: 'bold' }}
                suffix={
                  <div
                    style={{
                      fontSize: '12px',
                      color: 'rgba(255,255,255,0.85)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>{stats.activeCustomers} attivi</span>
                    <Tooltip title="Clienti con almeno un'attivita nel periodo selezionato">
                      <QuestionCircleOutlined style={{ color: 'rgba(255,255,255,0.85)' }} />
                    </Tooltip>
                  </div>
                }
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
    style={{
                borderRadius: 16,   background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                border: 'none'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Statistic style={{minHeight:100}}
                title="Attività Questa Settimana"
                value={stats.thisWeekActivities}
                loading={loading}
                prefix={<CalendarOutlined style={{ color: '#d4691a' }} />}
                valueStyle={{ color: '#d4691a', fontSize: '28px', fontWeight: 'bold' }}
                suffix={
                  weeklyTrend !== 0 && (
                    <Tag
                      color={weeklyTrend > 0 ? 'success' : 'error'}
                      style={{ fontSize: '10px' }}
                    >
                      
                </Tag>
                  )
                }
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
              style={{
                borderRadius: 16,   background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                border: 'none'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Statistic style={{minHeight:100}}
          
                title="Attività Questo Mese"
                value={stats.thisMonthActivities}
                loading={loading}
                prefix={<RiseOutlined style={{ color: '#1890ff' }} />}
                valueStyle={{ color: '#1890ff', fontSize: '28px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <Card
    style={{
                borderRadius: 16,   background: 'linear-gradient(135deg, #d299c2 0%, #fef9d7 100%)',
                border: 'none'
              }}
              bodyStyle={{ padding: '24px' }}
            >
              <Statistic style={{minHeight:100}}
                title="Membri del team"
                value={stats.teamMembers}
                loading={loading}
                prefix={<TeamOutlined style={{ color: '#722ed1' }} />}
                valueStyle={{ color: '#722ed1', fontSize: '28px', fontWeight: 'bold' }}
              />
            </Card>
          </Col>
        </Row>
        <Card
          style={{ borderRadius: 16 }}
          headStyle={{ borderBottom: '1px solid #f0f0f0' }}
          title={
            <span style={{ fontWeight: 600 }}>
              {activityView === 'customer' ? 'Attività per cliente' : 'Le mie attività'}
            </span>
          }
          extra={
            <Space size={12}>
              {activityView === 'customer' && (
                <Button size="small" type="link" onClick={() => navigate('/analytics')}>
                  Vai alla pagina analytics
                </Button>
              )}
              <Segmented
                size="small"
                value={activityView}
                onChange={(value) => setActivityView(value as 'customer' | 'mine')}
                options={[
                  { label: 'Per cliente', value: 'customer' },
                  { label: 'Le mie attività', value: 'mine' },
                ]}
              />
            </Space>
          }
        >
          {activityView === 'customer' ? (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <Space wrap size={12}>
                  <Input
                    allowClear
                    style={{ minWidth: 220 }}
                    placeholder="Cerca cliente"
                    prefix={<SearchOutlined />}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                  />
                  <RangePicker
                    allowClear
                    value={dateRange ?? undefined}
                    onChange={(value) => setDateRange(value as RangeValue)}
                    format="DD/MM/YYYY"
                  />
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Filtra per tipologia"
                    style={{ minWidth: 220 }}
                    value={typeFilter}
                    onChange={(value) => setTypeFilter(value as string[])}
                    options={typeOptions}
                    maxTagCount="responsive"
                  />
                </Space>

                <Space size={12}>
                  <Text type="secondary">
                    {tableData.length} {tableData.length === 1 ? 'cliente' : 'clienti'}
                  </Text>
                  {filtersApplied && (
                    <Button
                      type="link"
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        setDateRange(getCurrentMonthRange());
                        setTypeFilter([]);
                        setSearchTerm('');
                      }}
                    >
                      Reset filtri
                    </Button>
                  )}
                </Space>
              </div>

              <Table<CustomerActivityRow>
                rowKey="customerId"
                columns={columns}
                dataSource={tableData}
                loading={loading}
                onRow={(record) => ({
                  onClick: () => handleRowClick(record),
                  style: { cursor: 'pointer' },
                })}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} clienti`,
                }}
                scroll={{ x: 900 }}
              />
            </Space>
          ) : (
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                  alignItems: 'center',
                }}
              >
                <Space wrap size={12}>
                  <Input
                    allowClear
                    style={{ minWidth: 220 }}
                    placeholder="Cerca attività o cliente"
                    prefix={<SearchOutlined />}
                    value={mySearchTerm}
                    onChange={(event) => setMySearchTerm(event.target.value)}
                  />
                  <RangePicker
                    allowClear
                    value={myDateRange ?? undefined}
                    onChange={(value) => setMyDateRange(value as RangeValue)}
                    format="DD/MM/YYYY"
                  />
                  <Select
                    mode="multiple"
                    allowClear
                    placeholder="Filtra per tipologia"
                    style={{ minWidth: 220 }}
                    value={myTypeFilter}
                    onChange={(value) => setMyTypeFilter(value as string[])}
                    options={myTypeOptions}
                    maxTagCount="responsive"
                  />
                </Space>

                <Space size={12}>
                  <Text type="secondary">
                    {(myTypeFilter.length || mySearchTerm.trim().length ? myTableData.length : myTotal)} attivita
                  </Text>
                  {myFiltersApplied && (
                    <Button
                      type="link"
                      icon={<ReloadOutlined />}
                      onClick={() => {
                        setMyDateRange(getCurrentMonthRange());
                        setMyTypeFilter([]);
                        setMySearchTerm('');
                      }}
                    >
                      Reset filtri
                    </Button>
                  )}
                </Space>
              </div>

              <Table<ActivityDTO>
                rowKey="id"
                columns={myColumns}
                dataSource={myTableData}
                loading={loading}
                onRow={(record) => ({
                  onClick: () => openActivityDetails(record.id),
                  style: { cursor: 'pointer' },
                })}
                pagination={{
                  current: myPage,
                  pageSize: myPageSize,
                  total: myTypeFilter.length || mySearchTerm.trim().length ? myTableData.length : myTotal,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} di ${total} attivita`,
                  onChange: (page, pageSize) => {
                    setMyPage(page);
                    setMyPageSize(pageSize);
                  },
                }}
                scroll={{ x: 900 }}
              />
            </Space>
          )}
        </Card>
      </StandardPage>

      <ActivityDetailsDrawer
        open={detailsOpen}
        activityId={selectedActivityId ?? undefined}
        onClose={() => {
          setDetailsOpen(false);
          setSelectedActivityId(null);
        }}
        onRefresh={() => refreshAll()}
        canEdit={activityView === 'mine'}
      />

      <CreateActivityDrawer
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        onCreated={() => refreshAll()}
      />
    </>
  );
};

export default Dashboard;
